import React, { useState } from "react";
import { Box } from "@mui/system";
import { useNavigate } from "react-router-dom";
import CommCard from "src/common/CommCard";
import { CommonPagination } from "src/common/CommonPagination";
import { useGetQueryWithTotal } from "src/hooks/useGetQueryWithTotal";
import type { IVisitorListItem } from "../type/newVisitorType";
import VisitorTable from "./VisitorTable";
import CreateVisitor from "./CreateVisitor";
import UpdateVisitor from "./UpdateVisitor";
import DeleteVisitor from "./DeleteVisitor";

const NewVisitorList: React.FC = () => {
	const navigate = useNavigate();
	const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
	const [editModalState, setEditModalState] = useState<{
		open: boolean;
		id: number | null;
	}>({
		open: false,
		id: null,
	});
	const [deleteModalState, setDeleteModalState] = useState<{
		open: boolean;
		id: number | null;
		name: string;
	}>({
		open: false,
		id: null,
		name: "",
	});

	const [pagination, setPagination] = useState({
		limit: 10,
		skip: 0,
	});

	const [searchQuery, setSearchQuery] = useState<string>("");
	const [statusFilter, setStatusFilter] = useState<string>("");

	const { data, isLoading } = useGetQueryWithTotal<IVisitorListItem[]>({
		endPoint: "/api/v1/admin/visitor",
		queryKeys: ["Visitors", statusFilter, searchQuery, pagination.skip, pagination.limit],
		params: {
			limit: pagination.limit,
			skip: pagination.skip,
			...(searchQuery.trim() ? { searchQuery: searchQuery.trim() } : {}),
			...(statusFilter && statusFilter !== "all" ? { status: statusFilter } : {}),
		},
	});

	const handleView = (id: number) => {
		navigate(`/visitor/details/${id}`);
	};

	const handleEdit = (id: number) => {
		setEditModalState({ open: true, id });
	};

	const handleDelete = (id: number, name: string) => {
		setDeleteModalState({ open: true, id, name });
	};

	return (
		<>
			<CommCard
				showCardHeader
				showSearch
				showButton
				showStatus
				statusOptions={[
					{ id: "all", name: "All Status" },
					{ id: "pending", name: "Pending" },
					{ id: "approved", name: "Approved" },
					{ id: "rejected", name: "Rejected" },
				]}
				handleSelectStatus={(status) => {
					setStatusFilter(status === "all" ? "" : status);
					setPagination((prev) => ({ ...prev, skip: 0 }));
				}}
				searchPlaceHolder="Search by visitor name or phone..."
				btnText="Create Visitor"
				headerTitle={`Visitor List (${data?.total ?? 0})`}
				handleSearchFun={(query) => {
					setSearchQuery(query);
					setPagination((prev) => ({ ...prev, skip: 0 }));
				}}
				handleBtnForModalFun={() => setOpenCreateModal(true)}
				helmetTitle="BTTF - Visitor List"
			>
				{/* Visitor Table */}
				<VisitorTable
					data={data?.data || []}
					isLoading={isLoading}
					paginationSkip={pagination.skip}
					onView={handleView}
					onEdit={handleEdit}
					onDelete={handleDelete}
				/>

				{/* Pagination */}
				<Box p={2}>
					<CommonPagination
						total={data?.total}
						paginationCalFun={(limit: number, skip: number) => {
							setPagination({ limit, skip });
						}}
					/>
				</Box>

				{/* Create Visitor Modal */}
				<CreateVisitor
					open={openCreateModal}
					handleClose={() => setOpenCreateModal(false)}
				/>

				{/* Update Visitor Modal */}
				<UpdateVisitor
					open={editModalState.open}
					handleClose={() =>
						setEditModalState((prev) => ({ ...prev, open: false }))
					}
					visitorId={editModalState.id}
				/>

				{/* Delete Confirmation Dialog */}
				<DeleteVisitor
					open={deleteModalState.open}
					handleClose={() =>
						setDeleteModalState((prev) => ({ ...prev, open: false }))
					}
					visitorId={deleteModalState.id}
					visitorName={deleteModalState.name}
				/>
			</CommCard>
		</>
	);
};

export default NewVisitorList;
